import { createContext, useCallback, useContext, useMemo } from "react";
import { AuthManager, useAuthManager } from "../hooks/useAuthManager";
import { useDispatch } from "react-redux";
import { authenticateTwoFactor, setCurrentUser, setIsAuthLoading, setIsProfileLoading } from "../authSlice";
import { clearAllToastMessages, isPAPLUsers } from "utils/helpers";
import { apiV1 } from "api-v2";
import { navigate } from "@reach/router";
import { extractTwoFactorChallenge, getOrFetchXsrfToken } from "../utils";
import type { AuthenticateResponse } from "../types/AuthenticateResponse.type";
import { logErrorIfDebug } from "utils/logging";
import { NavigationPathsBase, useNavigationPaths } from "../hooks/useNavigationPaths";
import { useAppMenu } from "newui/features/AppMenu/hooks/useAppMenu";
import { ReferralTokenType } from "../components/Login";

export interface AuthenticateValues {
	[key: string]: unknown;
	email: string;
	inviteToken?: string | null;
	password: string;
	remember_me?: boolean;
	tokenType?: ReferralTokenType | null;
}

export type { AuthenticateResponse } from "../types/AuthenticateResponse.type";

interface AuthContextValue extends AuthManager, NavigationPathsBase {
	authenticateUser: (
		authData: AuthenticateResponse
	) => Promise<AuthenticateResponse>;
	loginUser: (values: AuthenticateValues) => Promise<AuthenticateResponse>;
}

export const AuthContext = createContext<AuthContextValue>(null);

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { errorMessage, isSuspended, isVerified, logoutUser } = useAuthManager();
	const { clientPaths, currentUserHomePage, guestPaths, memberPaths, supplierPaths } = useNavigationPaths();
	const { collapse } = useAppMenu();

	const dispatch = useDispatch();

	const authenticateUser = useCallback(
		async (authData: AuthenticateResponse): Promise<AuthenticateResponse> => {
			try {
				let user = authData?.user;

				if (!user) {
					throw new Error("User data is missing after authentication");
				}

				// authenticate user in store. `authenticateUser` only runs once the
				// second factor is already cleared (non-2FA login, or after the
				// challenge succeeds), so it no longer branches on 2FA or writes any
				// client-side 2FA session flag — the login challenge is owned by
				// `loginUser` off the server-authoritative `two_factor_required`.
				await dispatch(
					authenticateTwoFactor({
						two_factor_enabled: user?.two_factor_enabled,
						two_factor_verified: false,
					})
				);

				if (isPAPLUsers(user)) {
					// Forced 2FA enrolment for PAPL users now lands on the new
					// Settings → Security enrolment flow (Task 005). The legacy
					// `/two-factor-setup` reach-router route has been removed.
					if (memberPaths) {
						navigate(memberPaths.accountSettings.security);
					}
					return authData;
				}

				if (!user.company && !user.is_guest) {
					const userProfileResponse = await apiV1.get("auth/user-profile");
					user = userProfileResponse?.data?.data;
				}

				collapse(false);

				dispatch(setCurrentUser(user));

				return authData;
			} catch (error) {
				logErrorIfDebug("Authentication failed:", error);
				throw error;
			}
		},
		[collapse, dispatch, memberPaths]
	);

	const loginUser = useCallback(
		async (values: AuthenticateValues): Promise<AuthenticateResponse> => {
			try {
				await getOrFetchXsrfToken();
				const userResponse = await apiV1.post<AuthenticateResponse>(
					"auth/login",
					{
						...values,
					}
				);

				clearAllToastMessages();

				// Server-authoritative 2FA gate. Both the member (raw) and guest
				// (ApiResponseDTO envelope) login responses are normalised here; when
				// a challenge is pending we stop BEFORE establishing any session and
				// surface the pending state so the login wrapper can render the
				// in-place challenge step. Never re-derived client-side.
				const challenge = extractTwoFactorChallenge(userResponse.data);
				if (challenge.twoFactorRequired) {
					return {
						...userResponse.data,
						success: userResponse.data?.success ?? true,
						two_factor_required: true,
						mfa_token: challenge.mfaToken,
						available_methods: challenge.availableMethods,
					};
				}

				dispatch(setIsProfileLoading(true));
				dispatch(setIsAuthLoading(true));
				return await authenticateUser(userResponse.data);
			} catch (error) {
				console.error("Login failed:", error);
				dispatch(setIsProfileLoading(false));
				throw error;
			} finally {
				dispatch(setIsAuthLoading(false));
			}
		},
		[authenticateUser, dispatch]
	);

	const contextValue = useMemo(() => ({
		clientPaths,
		guestPaths,
		memberPaths,
		currentUserHomePage,
		supplierPaths,
		errorMessage,
		isSuspended,
		isVerified,
		authenticateUser,
		loginUser,
		logoutUser
	}), [
		clientPaths,
		guestPaths,
		memberPaths,
		currentUserHomePage,
		supplierPaths,
		errorMessage,
		isSuspended,
		isVerified,
		authenticateUser,
		loginUser,
		logoutUser
	]);

	return (
		<AuthContext.Provider
			value={contextValue}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuthContext = (): AuthContextValue => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuthContext must be used within AuthContextProvider");
	}
	return context;
};
